import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { useTranslation } from 'react-i18next';
import ShopHeader from '../../components/customer/ShopHeader';
import Loader from '../../components/common/Loader';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUrl';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const { t, i18n } = useTranslation(); // ✅ ADDED: Get i18n for language
  
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'alert',
    title: '',
    message: '',
    onConfirm: null
  });

  // ✅ ADDED: Re-fetch when language changes
  useEffect(() => {
    fetchProduct();
  }, [slug, i18n.language]); // ✅ Added i18n.language dependency

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      // ✅ FIXED: Add language parameter
      const response = await axios.get(`https://kid-o.ge/api/products/${slug}`, {
        params: {
          lang: i18n.language // ✅ Pass current language (ka, en, or ru)
        }
      });
      setProduct(response.data.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      showModal(
        'error',
        t('product.messages.error') || 'Error',
        t('product.messages.productNotFound') || 'Product not found',
        () => navigate('/shop')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    // Check if user is logged in
    if (!isAuthenticated) {
      showModal(
        'error',
        t('product.messages.loginRequired') || 'Login Required',
        t('product.messages.loginRequiredDesc') || 'Please login to add items to cart',
        () => navigate('/login')
      );
      return;
    }

    addToCart(product, quantity);
    showModal(
      'success',
      t('product.messages.addedToCart') || 'Added to Cart',
      `${quantity} x ${product.name} ${t('product.messages.addedToCartDesc') || 'added to cart'}`
    );
    setQuantity(1); // Reset quantity after adding
  };

  if (loading) return <Loader fullscreen />;
  if (!product) return null;

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [{ image_url: product.primary_image }];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--light)' }}>      
      <div className="product-detail-container">
        <div className="breadcrumb">
          <a onClick={() => navigate('/shop')}>{t('header.products') || 'Shop'}</a>
          <span> / </span>
          {product.category_name && (
            <>
              <a onClick={() => navigate(`/shop?category=${product.category_slug}`)}>
                {product.category_name}
              </a>
              <span> / </span>
            </>
          )}
          <span>{product.name}</span>
        </div>

        <div className="product-detail">
          <div className="product-gallery">
            <div className="main-image-wrapper">
              {images[selectedImage] ? (
                <img
                  src={getImageUrl(images[selectedImage].image_url)}
                  alt={product.name}
                  className="main-image"
                />
              ) : (
                <div className="main-image" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '5rem',
                  color: 'var(--gray-light)'
                }}>
                  📦
                </div>
              )}
            </div>

            {images.length > 1 && (
              <div className="thumbnail-list">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={getImageUrl(image.image_url)}
                      alt={`${product.name} ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="product-detail-info">
            {product.category_name && (
              <div className="product-detail-category">{product.category_name}</div>
            )}
            <h1 className="product-detail-name">{product.name}</h1>
            <div className="product-detail-price">₾{parseFloat(product.price).toFixed(2)}</div>
            
            {product.description && (
              <p className="product-detail-description">{product.description}</p>
            )}

            <div className="product-meta">
              <div className="meta-item">
                <span className="meta-label">SKU:</span>
                <span className="meta-value">{product.sku || 'N/A'}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">{t('product.availability') || 'Availability'}:</span>
                <span className="meta-value" style={{
                  color: product.stock > 0 ? 'var(--success)' : 'var(--error)'
                }}>
                  {product.stock > 0 ? t('product.inStock') : t('product.outOfStock')}
                </span>
              </div>
              {product.category_name && (
                <div className="meta-item">
                  <span className="meta-label">{t('filter.category') || 'Category'}:</span>
                  <span className="meta-value">{product.category_name}</span>
                </div>
              )}
              {/* ✅ ADDED: Show brand if exists */}
              {product.brand_name && (
                <div className="meta-item">
                  <span className="meta-label">{t('filter.brand') || 'Brand'}:</span>
                  <span className="meta-value">{product.brand_name}</span>
                </div>
              )}
            </div>

            {product.stock > 0 && (
              <>
                <div className="quantity-selector">
                  <span className="quantity-label">{t('product.quantity') || 'Quantity'}:</span>
                  <div className="quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      className="quantity-input"
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val >= 1 && val <= product.stock) {
                          setQuantity(val);
                        }
                      }}
                      min="1"
                      max={product.stock}
                    />
                    <button
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= product.stock}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="add-to-cart-section">
                  <button className="add-to-cart-btn" onClick={handleAddToCart}>
                    <span>🛒</span>
                    <span>{t('product.addToCart') || 'Add to Cart'}</span>
                  </button>
                </div>
              </>
            )}

            {product.stock === 0 && (
              <button className="add-to-cart-btn" disabled>
                {t('product.outOfStock') || 'Out of Stock'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        type={modal.type}
        title={modal.title}
        message={modal.message}
      />
    </div>
  );
};

export default ProductDetail;